import React, { useState, useEffect } from 'react';
import {
  Box,
  // Container - managed via Box layout
  Typography,
  Button,
  Grid,
  Card,
  Autocomplete,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  FlightTakeoff,
  LocationOn,
  AccessTime,
  AttachMoney,
  Description,
  CheckCircle,
  Warning,
  Info,
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { visaService, Country, VisaCheckRequest, VisaCheckResponse } from '../../services/visaService';
import { useAuth } from '../../contexts/AuthContext';

const VisaCheckPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // State
  const [countries, setCountries] = useState<Country[]>([]);
  const [fromCountry, setFromCountry] = useState<Country | null>(null);
  const [toCountry, setToCountry] = useState<Country | null>(null);
  const [visaType, setVisaType] = useState<string>('tourist');
  const [purpose, setPurpose] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [result, setResult] = useState<VisaCheckResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Helper: generate flag emoji from ISO2 code
  const getFlagEmoji = (iso2: string) => {
    if (!iso2 || iso2.length !== 2) return '';
    try {
      return String.fromCodePoint(...Array.from(iso2.toUpperCase()).map(c => 127397 + c.charCodeAt(0)));
    } catch { return ''; }
  };

  // Load countries on component mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await visaService.getCountries();
        const list = Array.isArray(response.data)
          ? response.data
          : Array.isArray((response.data as any)?.countries)
            ? (response.data as any).countries
            : [];
        if (response.success && list.length > 0) {
          // Normalize API response: backend returns iso2/iso3, interface expects code/flag
          const normalized = list.map((c: any) => ({
            ...c,
            code: c.code || c.iso2 || c.iso3 || '',
            flag: c.flag || c.flagEmoji || getFlagEmoji(c.iso2 || '') || '',
          }));
          setCountries(normalized);
        } else {
          setError(response.message || 'Failed to load countries');
        }
      } catch (err) {
        setError('Failed to load countries');
        console.error('Error loading countries:', err);
      } finally {
        setCountriesLoading(false);
      }
    };

    loadCountries();
  }, []);

  const handleVisaCheck = async () => {
    if (!fromCountry || !toCountry) {
      setError('Please select both origin and destination countries');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const request: VisaCheckRequest = {
        fromCountry: fromCountry.code,
        toCountry: toCountry.code,
        visaType,
        purpose: purpose || undefined,
      };

      const response = await visaService.checkVisaRequirements(request);
      
      if (response.success && response.data) {
        setResult(response.data);
      } else {
        setError(response.message || (response as any).error || 'Failed to check visa requirements');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to check visa requirements');
      console.error('Visa check error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartApplication = () => {
    const params = new URLSearchParams();
    if (toCountry?.code) params.set('country', toCountry.code);
    const queryString = params.toString();
    const target = `/applications/new${queryString ? `?${queryString}` : ''}`;

    if (isAuthenticated) {
      navigate(target);
    } else {
      navigate('/login', {
        state: {
          returnTo: target,
          applicationData: {
            fromCountry: fromCountry?.code,
            toCountry: toCountry?.code,
            visaType,
            purpose,
          },
        },
      });
    }
  };

  const visaTypeOptions = [
    { value: 'tourist', label: 'Tourist/Visitor' },
    { value: 'business', label: 'Business' },
    { value: 'student', label: 'Student' },
    { value: 'work', label: 'Work/Employment' },
    { value: 'family', label: 'Family/Dependent' },
    { value: 'transit', label: 'Transit' },
    { value: 'medical', label: 'Medical' },
  ];

  return (
    <Box sx={{ bgcolor: '#030B1B', minHeight: '100vh' }}>
      {/* ===== Hero ===== */}
      <Box sx={{ background: 'linear-gradient(180deg, rgba(3,11,27,0.7) 0%, rgba(3,11,27,0.95) 100%), url("https://images.unsplash.com/photo-1436491865332-7a61a109db05?w=1920&q=80")', backgroundSize: 'cover', backgroundPosition: 'center', color: '#fff', minHeight: 200, pt: { xs: 4, md: 5 }, pb: { xs: 6, md: 7 }, px: { xs: 2, md: 4 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.6)' }} />} sx={{ mb: 3 }}>
            <Link underline="hover" onClick={() => navigate('/')} sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', cursor: 'pointer' }}>
              <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} /> Home
            </Link>
            <Link underline="hover" onClick={() => navigate('/visa-check')} sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', cursor: 'pointer' }}>Visa Services</Link>
            <Typography sx={{ color: '#fff', fontWeight: 500, fontSize: '0.85rem' }}>Check Requirements</Typography>
          </Breadcrumbs>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, letterSpacing: '-0.02em', textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>Visa Requirements Checker</Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, fontWeight: 400, textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>Get instant visa requirements for your travel destination</Typography>
        </Box>
      </Box>

      {/* ===== Content ===== */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: -4, px: { xs: 2, md: 4 }, pb: 6 }}>

      <Grid container spacing={4}>
        {/* Visa Check Form */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, boxShadow: '0 4px 30px rgba(0,0,0,0.3)', backdropFilter: 'blur(12px)' }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'white' }}>
              Check Requirements
            </Typography>

            <Grid container spacing={3}>
              {/* From Country */}
              <Grid item xs={12}>
                <Autocomplete
                  options={countries}
                  getOptionLabel={(option) => `${option.flag || ''} ${option.name}`}
                  value={fromCountry}
                  onChange={(_, newValue) => setFromCountry(newValue)}
                  loading={countriesLoading}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, color: 'white', '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' }, '&.Mui-focused fieldset': { borderColor: '#0EA5E9' } }, '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)' }, '& .MuiInputBase-input': { color: 'white' }, '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.4)' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' }, '& .MuiInputLabel-root.Mui-focused': { color: '#0EA5E9' } }}
                  componentsProps={{ paper: { sx: { bgcolor: '#1E293B', color: 'white', border: '1px solid rgba(255,255,255,0.1)' } } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="From Country (Your Nationality)"
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <LocationOn sx={{ mr: 1, color: 'rgba(255,255,255,0.4)' }} />,
                        endAdornment: (
                          <>
                            {countriesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* To Country */}
              <Grid item xs={12}>
                <Autocomplete
                  options={countries}
                  getOptionLabel={(option) => `${option.flag || ''} ${option.name}`}
                  value={toCountry}
                  onChange={(_, newValue) => setToCountry(newValue)}
                  loading={countriesLoading}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, color: 'white', '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' }, '&.Mui-focused fieldset': { borderColor: '#0EA5E9' } }, '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)' }, '& .MuiInputBase-input': { color: 'white' }, '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.4)' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' }, '& .MuiInputLabel-root.Mui-focused': { color: '#0EA5E9' } }}
                  componentsProps={{ paper: { sx: { bgcolor: '#1E293B', color: 'white', border: '1px solid rgba(255,255,255,0.1)' } } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="To Country (Destination)"
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <FlightTakeoff sx={{ mr: 1, color: 'rgba(255,255,255,0.4)' }} />,
                        endAdornment: (
                          <>
                            {countriesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Visa Type */}
              <Grid item xs={12}>
                <Autocomplete
                  options={visaTypeOptions}
                  getOptionLabel={(option) => option.label}
                  value={visaTypeOptions.find(opt => opt.value === visaType) || visaTypeOptions[0]}
                  onChange={(_, newValue) => setVisaType(newValue?.value || 'tourist')}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, color: 'white', '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' }, '&.Mui-focused fieldset': { borderColor: '#0EA5E9' } }, '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)' }, '& .MuiInputBase-input': { color: 'white' }, '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.4)' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' }, '& .MuiInputLabel-root.Mui-focused': { color: '#0EA5E9' } }}
                  componentsProps={{ paper: { sx: { bgcolor: '#1E293B', color: 'white', border: '1px solid rgba(255,255,255,0.1)' } } }}
                  renderInput={(params) => (
                    <TextField {...params} label="Purpose of Visit" required />
                  )}
                />
              </Grid>

              {/* Purpose Details */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Additional Details (Optional)"
                  placeholder="e.g., Conference name, university, company..."
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  multiline
                  rows={2}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, color: 'white', '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' }, '&.Mui-focused fieldset': { borderColor: '#0EA5E9' } }, '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)' }, '& .MuiInputBase-input': { color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' }, '& .MuiInputLabel-root.Mui-focused': { color: '#0EA5E9' } }}
                />
              </Grid>

              {/* Error Display */}
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}

              {/* Check Button */}
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleVisaCheck}
                  disabled={loading || !fromCountry || !toCountry}
                  startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                  sx={{ py: 1.5, background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)', fontWeight: 600, fontSize: '1rem', borderRadius: 2, '&:hover': { background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)', opacity: 0.9 }, '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)' } }}
                >
                  {loading ? 'Checking Requirements...' : 'Check Visa Requirements'}
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Results */}
        <Grid item xs={12} md={6}>
          {result && (
            <Card sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, boxShadow: '0 4px 30px rgba(0,0,0,0.3)', backdropFilter: 'blur(12px)' }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'white' }}>
                Visa Requirements
              </Typography>

              {/* Visa Required Status */}
              <Paper 
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  bgcolor: result.isRequired ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.15)',
                  color: result.isRequired ? '#F59E0B' : '#10B981',
                  border: result.isRequired ? '1px solid rgba(245,158,11,0.25)' : '1px solid rgba(16,185,129,0.25)',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {result.isRequired ? (
                    <Warning sx={{ mr: 1 }} />
                  ) : (
                    <CheckCircle sx={{ mr: 1 }} />
                  )}
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {result.isRequired ? 'Visa Required' : 'No Visa Required'}
                  </Typography>
                </Box>
                <Typography variant="body2">
                  {result.isRequired 
                    ? `You need a ${result.visaType} visa to travel to ${toCountry?.name}`
                    : `You can travel to ${toCountry?.name} without a visa`
                  }
                </Typography>
              </Paper>

              {result.isRequired && (
                <>
                  {/* Processing Details */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccessTime sx={{ mr: 1, color: '#0EA5E9' }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                            Processing Time
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>
                            {result.processingTime}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AttachMoney sx={{ mr: 1, color: '#10B981' }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                            Fee
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>
                            ${result.fee}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Required Documents */}
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'white' }}>
                    Required Documents
                  </Typography>
                  <List sx={{ mb: 3 }}>
                    {result.documents.map((document, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon>
                          <Description sx={{ color: '#0EA5E9' }} />
                        </ListItemIcon>
                        <ListItemText primary={document} sx={{ '& .MuiListItemText-primary': { color: 'rgba(255,255,255,0.7)' } }} />
                      </ListItem>
                    ))}
                  </List>

                  {/* Recommendations */}
                  {result.recommendations && result.recommendations.length > 0 && (
                    <>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'white' }}>
                        Recommendations
                      </Typography>
                      <List sx={{ mb: 3 }}>
                        {result.recommendations.map((recommendation, index) => (
                          <ListItem key={index} sx={{ py: 0.5 }}>
                            <ListItemIcon>
                              <Info sx={{ color: '#8B5CF6' }} />
                            </ListItemIcon>
                            <ListItemText primary={recommendation} sx={{ '& .MuiListItemText-primary': { color: 'rgba(255,255,255,0.7)' } }} />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}

                  {/* Action Button */}
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleStartApplication}
                    sx={{ py: 1.5, background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)', fontWeight: 600, fontSize: '1rem', borderRadius: 2, '&:hover': { background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)', opacity: 0.9 } }}
                  >
                    Start Application
                  </Button>
                </>
              )}
            </Card>
          )}

          {!result && !loading && (
            <Card sx={{ p: 4, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, boxShadow: '0 4px 30px rgba(0,0,0,0.3)', backdropFilter: 'blur(12px)' }}>
              <FlightTakeoff sx={{ fontSize: 60, color: '#0EA5E9', mb: 2 }} />
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                Select countries and click "Check Visa Requirements" to see detailed requirements
              </Typography>
            </Card>
          )}
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

export default VisaCheckPage;
